import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Check,
  Sparkles,
  Crown,
  Mail,
  ShieldCheck,
  Zap,
  CreditCard,
  RefreshCw,
  AlertCircle,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Static per-plan visuals (icon, features, urlKey). Prices are merged
// in from the admin's Price Plans config on mount so admins can update
// numbers without a redeploy.
const PLAN_DEFAULTS = [
  {
    id: "monthly",
    name: "1 Month",
    price: "$4.99",
    priceHint: "per device",
    icon: Zap,
    urlKey: "purchase_monthly_url",
    features: [
      "One-device activation",
      "Full app on every platform",
      "Priority support",
      "Cancel anytime",
    ],
  },
  {
    id: "yearly",
    name: "1 Year",
    price: "$29.99",
    priceHint: "per device · save 50%",
    icon: Sparkles,
    urlKey: "purchase_yearly_url",
    highlight: true,
    features: [
      "One-device activation",
      "Free updates all year",
      "Priority support",
      "Best value",
    ],
  },
  {
    id: "lifetime",
    name: "Lifetime",
    price: "$79.99",
    priceHint: "one-time · never expires",
    icon: Crown,
    urlKey: "purchase_lifetime_url",
    features: [
      "One-device activation",
      "Every future update, forever",
      "Priority support",
      "Transferable to a new device once",
    ],
  },
];

const _fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return null; }
};

function PaymentBanner({ status, settlement, error, onDismiss }) {
  if (status === "cancelled") {
    return (
      <div
        className="flex items-start gap-3 rounded-2xl border border-yellow-500/30 bg-yellow-500/10 p-5 text-yellow-50"
        data-testid="pay-banner-cancelled"
      >
        <XCircle className="mt-0.5 h-5 w-5 flex-none text-yellow-300" />
        <div className="flex-1">
          <div className="text-base font-semibold">Payment cancelled</div>
          <div className="mt-1 text-sm text-yellow-100/80">
            No charge was made. You can try again below or contact support if you need help.
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="ml-2 rounded-md px-2 py-1 text-xs text-yellow-100/60 hover:bg-yellow-500/10 hover:text-yellow-50"
          data-testid="pay-banner-dismiss"
        >
          Dismiss
        </button>
      </div>
    );
  }

  // status === "paid"
  const s = (settlement?.status || "").toLowerCase();
  const isCompleted = s === "completed";
  const isFailed = ["signature_failed", "validate_failed", "amount_mismatch", "settle_failed"].includes(s);
  const isProcessing = !settlement || (!isCompleted && !isFailed && !error);

  if (isFailed) {
    return (
      <div
        className="flex items-start gap-3 rounded-2xl border border-red-500/40 bg-red-500/10 p-5 text-red-50"
        data-testid="pay-banner-failed"
      >
        <XCircle className="mt-0.5 h-5 w-5 flex-none text-red-300" />
        <div className="flex-1">
          <div className="text-base font-semibold">Payment could not be verified</div>
          <div className="mt-1 text-sm text-red-100/80">
            PayFast reported an issue verifying this payment ({s.replace(/_/g, " ")}).
            If money left your account, please contact support with reference{" "}
            <span className="font-mono text-red-100">{settlement?.m_payment_id}</span>.
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="ml-2 rounded-md px-2 py-1 text-xs text-red-100/70 hover:bg-red-500/10 hover:text-red-50"
          data-testid="pay-banner-dismiss"
        >
          Dismiss
        </button>
      </div>
    );
  }

  if (isCompleted) {
    const newExp = settlement?.new_expires_at ? _fmtDate(settlement.new_expires_at) : null;
    const purpose = settlement?.purpose;
    const key = settlement?.activation_key || settlement?.new_activation_key;
    const isNewActivation = purpose === "activation" && !!settlement?.new_activation_key;
    return (
      <div
        className="flex items-start gap-3 rounded-2xl border border-emerald-400/40 bg-emerald-500/10 p-5 text-emerald-50 shadow-[0_20px_60px_-20px_rgba(74,222,128,0.35)]"
        data-testid="pay-banner-success"
      >
        <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-emerald-300" />
        <div className="flex-1">
          <div className="text-base font-semibold">
            {isNewActivation ? "Payment received — your key is ready!" : "Payment received — thank you!"}
          </div>
          <div className="mt-1 text-sm text-emerald-100/85">
            {isNewActivation ? (
              <>
                Copy the key below into the ZA Player Pro app on your device
                {settlement?.mac_address ? <> (<span className="font-mono">{settlement.mac_address}</span>)</> : null}
                {newExp ? <> — it&apos;s valid until <span className="font-semibold text-white">{newExp}</span>.</> : "."}
              </>
            ) : purpose === "renew" && key ? (
              <>
                Your key <span className="font-mono text-white">{key}</span> has been renewed
                {newExp ? <> until <span className="font-semibold text-white">{newExp}</span></> : null}.
                Open the app and pull-to-refresh — the new expiry will show up automatically.
              </>
            ) : purpose === "credits" ? (
              <>Your reseller credits have been topped up. Head back to your reseller dashboard to see the new balance.</>
            ) : (
              <>Your payment has been confirmed. You can now activate the app.</>
            )}
          </div>

          {isNewActivation && key && (
            <div className="mt-3 flex items-center gap-2 rounded-xl border border-emerald-400/40 bg-black/40 p-3" data-testid="pay-banner-new-key">
              <span className="flex-1 truncate font-mono text-xl tracking-widest text-white">{key}</span>
              <button
                type="button"
                onClick={() => {
                  try { navigator.clipboard.writeText(key); } catch { /* noop */ }
                }}
                className="rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-black hover:bg-emerald-400"
                data-testid="pay-banner-copy-key"
              >
                Copy
              </button>
            </div>
          )}

          {settlement?.m_payment_id && (
            <div className="mt-2 text-[11px] text-emerald-100/60">
              Reference:{" "}
              <span className="font-mono">{settlement.m_payment_id}</span>
            </div>
          )}
        </div>
        <button
          onClick={onDismiss}
          className="ml-2 rounded-md px-2 py-1 text-xs text-emerald-100/70 hover:bg-emerald-500/10 hover:text-emerald-50"
          data-testid="pay-banner-dismiss"
        >
          Dismiss
        </button>
      </div>
    );
  }

  // isProcessing — polling in progress
  return (
    <div
      className="flex items-start gap-3 rounded-2xl border border-emerald-400/30 bg-emerald-500/5 p-5 text-emerald-50"
      data-testid="pay-banner-processing"
    >
      <Clock className="mt-0.5 h-5 w-5 flex-none animate-pulse text-emerald-300" />
      <div className="flex-1">
        <div className="text-base font-semibold">
          Payment received — finalising your account…
        </div>
        <div className="mt-1 text-sm text-emerald-100/75">
          PayFast is confirming the transaction with our server. This normally takes
          just a few seconds — hold tight, this page will update automatically.
        </div>
        {error && (
          <div className="mt-2 text-[11px] text-red-200/80">
            Verification hiccup: {error}. Refresh in a moment to retry.
          </div>
        )}
        {settlement?.m_payment_id && (
          <div className="mt-2 text-[11px] text-emerald-100/60">
            Reference:{" "}
            <span className="font-mono">{settlement.m_payment_id}</span>
          </div>
        )}
      </div>
      <Loader2 className="ml-2 h-4 w-4 flex-none animate-spin text-emerald-300" />
    </div>
  );
}

export default function Activation() {
  const [cfg, setCfg] = useState(null);
  const [paygate, setPaygate] = useState({ paypal_enabled: false, stripe_enabled: false, payfast_enabled: false });
  // PLANS is derived from admin config once the API resolves. Until
  // then we show the defaults so the page doesn't flash empty prices.
  const [PLANS, setPlans] = useState(PLAN_DEFAULTS);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // v1.4.17 — Inline renewal on the "Already have a key?" panel.
  // Buyer enters their key, picks a plan, and hits Renew. We validate
  // the key exists via /api/trial/lookup (no auth needed) and then
  // open the same gateway picker used for new purchases, passing the
  // key through so the checkout URL carries it as `?key=…` for the
  // admin to reconcile on payment success.
  const [renewKey, setRenewKey] = useState("");
  const [renewPlanId, setRenewPlanId] = useState("yearly");
  const [renewBusy, setRenewBusy] = useState(false);
  const [renewError, setRenewError] = useState("");
  const [renewInfo, setRenewInfo] = useState(null); // { activation_key, expires_at, days_remaining, is_expired }
  const renewPlan = PLANS.find((p) => p.id === renewPlanId);

  // v1.4.18 — PayFast return-URL handling.
  // After a successful (or cancelled) payment PayFast bounces the buyer to
  // /activation?ref=<m_payment_id>&status=paid|cancelled. We surface a
  // banner immediately, then poll /api/paygate/payfast/status/<ref> so the
  // buyer sees the payment transition from "processing" → "completed" as
  // the ITN webhook lands, and — for a renewal — the new expiry date.
  const [payStatus, setPayStatus] = useState(null);   // "paid" | "cancelled" | null
  const [payRef, setPayRef] = useState(null);         // m_payment_id from URL
  const [paySettlement, setPaySettlement] = useState(null); // server doc
  const [paySettleErr, setPaySettleErr] = useState("");

  // v1.4.19 — MAC address of the paying device.
  // Captured up-front so we know which device the fresh activation key
  // belongs to (and can preview it back to the buyer on success). Persisted
  // to localStorage so the field is pre-filled on the next visit.
  const [buyerMac, setBuyerMac] = useState(() => {
    try { return localStorage.getItem("zapp_buyer_mac") || ""; } catch { return ""; }
  });
  const [macError, setMacError] = useState("");
  const macNormalised = (buyerMac || "").replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
  const macValid = /^[0-9A-F]{12}$/.test(macNormalised);
  const macFormatted = macNormalised.match(/.{1,2}/g)?.join(":") || "";

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const status = params.get("status");
    const ref = params.get("ref");
    if (!status) return;
    setPayStatus(status);
    setPayRef(ref || null);
    // Scrub the URL so a refresh doesn't re-trigger the banner.
    try {
      const clean = window.location.pathname;
      window.history.replaceState({}, "", clean);
    } catch { /* noop */ }
  }, []);

  // Poll the settlement endpoint until it hits a terminal status.
  useEffect(() => {
    if (payStatus !== "paid" || !payRef) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 20; // ~60s at 3s intervals

    const tick = async () => {
      attempts += 1;
      try {
        const r = await fetch(`${BACKEND}/api/paygate/payfast/status/${encodeURIComponent(payRef)}`);
        if (!r.ok) {
          if (r.status === 404) {
            setPaySettleErr("Payment reference not recognised.");
            return; // stop
          }
          throw new Error(`status ${r.status}`);
        }
        const data = await r.json();
        if (cancelled) return;
        setPaySettlement(data);
        const s = (data.status || "").toLowerCase();
        // Terminal states — stop polling.
        if (["completed", "signature_failed", "validate_failed", "amount_mismatch", "settle_failed"].includes(s)) {
          return;
        }
        if (attempts < maxAttempts) {
          setTimeout(tick, 3000);
        }
      } catch (err) {
        if (attempts < maxAttempts && !cancelled) {
          setTimeout(tick, 3000);
        } else if (!cancelled) {
          setPaySettleErr(err?.message || "Could not verify payment status.");
        }
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [payStatus, payRef]);

  useEffect(() => {
    document.title = "Activation — ZA Player Pro";
    fetch(`${BACKEND}/api/public/website-config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setCfg(data))
      .catch(() => {});
    fetch(`${BACKEND}/api/public/paygate-status`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setPaygate(data))
      .catch(() => {});
    // v1.4.17 — merge admin-configured prices onto PLAN_DEFAULTS so
    // changes in /admin/price-plans surface here without a redeploy.
    // Enabled=false plans are dropped entirely.
    fetch(`${BACKEND}/api/public/price-plans`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data?.client_plans?.length) return;
        const merged = PLAN_DEFAULTS
          .map((d) => {
            const admin = data.client_plans.find((p) => p.id === d.id);
            if (!admin) return null; // plan removed by admin
            return {
              ...d,
              name: admin.name || d.name,
              price: admin.price_display || d.price,
              priceHint: admin.price_hint ?? d.priceHint,
            };
          })
          .filter(Boolean);
        // Any admin-configured plans NOT in defaults just get default visuals.
        for (const admin of data.client_plans) {
          if (!merged.some((m) => m.id === admin.id)) {
            merged.push({
              id: admin.id,
              name: admin.name,
              price: admin.price_display,
              priceHint: admin.price_hint,
              icon: Sparkles,
              urlKey: `purchase_${admin.id}_url`,
              features: ["One-device activation", "Full app on every platform"],
            });
          }
        }
        if (merged.length) setPlans(merged);
      })
      .catch(() => {});
  }, []);

  const contactEmail = cfg?.contact_email || "support@zaplayerpro.club";
  const anyGatewayEnabled = paygate.paypal_enabled || paygate.stripe_enabled || paygate.payfast_enabled;

  // Build the URL to send the buyer to for a given gateway + plan.
  // If `renewKeyStr` is provided, tag it onto every URL so the admin
  // can reconcile the payment to the correct activation key.
  const gatewayUrl = (gateway, plan, renewKeyStr = null) => {
    // Admin can override per-gateway via cfg fields (added later); for now
    // we route through the shared plan purchase URL, tagged with the
    // gateway so the admin can distinguish which button was clicked.
    const base = cfg?.[plan.urlKey];
    if (base) {
      try {
        const u = new URL(base);
        u.searchParams.set("gateway", gateway);
        u.searchParams.set("plan", plan.id);
        if (renewKeyStr) u.searchParams.set("renew_key", renewKeyStr);
        else if (macNormalised) u.searchParams.set("mac", macNormalised);
        return u.toString();
      } catch {
        return base;
      }
    }
    // PayPal-specific fallback: business-email checkout link.
    if (gateway === "paypal" && paygate.paypal_business_email) {
      const params = new URLSearchParams({
        cmd: "_xclick",
        business: paygate.paypal_business_email,
        item_name: renewKeyStr
          ? `ZA Player Pro renewal (${renewKeyStr}) — ${plan.name}`
          : `ZA Player Pro — ${plan.name} — ${macNormalised}`,
        amount: plan.price.replace(/[^0-9.]/g, ""),
        currency_code: paygate.paypal_currency || "USD",
        ...(renewKeyStr ? { custom: renewKeyStr } : { custom: macNormalised }),
      });
      return `https://www.paypal.com/cgi-bin/webscr?${params}`;
    }
    // PayFast: server builds a signed, FX-converted redirect payload.
    // We handle that in the click handler (below) rather than returning
    // a URL, since it requires an async fetch + form POST.
    if (gateway === "payfast") {
      return "#payfast"; // sentinel — actual submission happens in onClick
    }
    return `mailto:${contactEmail}?subject=${encodeURIComponent(
      renewKeyStr
        ? `Renew activation ${renewKeyStr} — ${plan.name}`
        : `Purchase ${plan.name} activation via ${gateway}`,
    )}`;
  };

  // Server-signed PayFast redirect. Calls /api/paygate/payfast/build
  // to get a signed field set (with USD→ZAR converted amount), then
  // auto-submits an HTML form to sandbox/live PayFast. This is what
  // the official WooCommerce plugin does and what PayFast expects.
  const submitPayfast = async (plan) => {
    setSelectedPlan(null);
    // A new activation requires a MAC. Renewals don't (the key implies device).
    if (!plan.renewKey && !macValid) {
      setMacError("Enter your device MAC address before paying.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    try {
      const amount = parseFloat(String(plan.price).replace(/[^0-9.]/g, "")) || 0;
      const res = await fetch(`${BACKEND}/api/paygate/payfast/build`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          purpose: plan.renewKey ? "renew" : "activation",
          amount,
          source_currency: "USD",
          item_name: plan.renewKey
            ? `ZA Player Pro renewal (${plan.renewKey}) — ${plan.name}`
            : `ZA Player Pro — ${plan.name} — ${macNormalised}`,
          activation_key: plan.renewKey || null,
          plan_id: plan.id,
          mac_address: plan.renewKey ? null : macNormalised,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data?.detail || "Could not start PayFast checkout.");
        return;
      }
      // Auto-submit form. PayFast requires POST, not GET.
      const form = document.createElement("form");
      form.method = "POST";
      form.action = data.action_url;
      form.style.display = "none";
      Object.entries(data.fields).forEach(([k, v]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = v == null ? "" : String(v);
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      alert(err?.message || "Network error contacting PayFast.");
    }
  };


  // Renewal handler — validates the key, then opens the same gateway
  // picker used for new purchases. `selectedPlan` carries a `renewKey`
  // marker so the picker knows to pass it through to gatewayUrl().
  const submitRenew = async (e) => {
    e.preventDefault();
    setRenewError("");
    setRenewInfo(null);
    const k = renewKey.trim().toUpperCase();
    if (!k) {
      setRenewError("Enter your activation key.");
      return;
    }
    if (!anyGatewayEnabled) {
      setRenewError("No payment gateway is configured. Please contact support.");
      return;
    }
    setRenewBusy(true);
    try {
      const res = await fetch(`${BACKEND}/api/trial/lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ activation_key: k }),
      });
      const data = await res.json();
      if (!res.ok) {
        setRenewError(data?.detail || "Key not found.");
        return;
      }
      setRenewInfo(data);
      // Fire the gateway picker with the renewal context attached.
      setSelectedPlan({ ...renewPlan, renewKey: data.activation_key });
    } catch (err) {
      setRenewError(err?.message || "Network error.");
    } finally {
      setRenewBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white antialiased" data-testid="activation-page">
      {/* Header */}
      <header className="border-b border-white/5">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3" data-testid="activation-home-link">
            <img src="/za-logo-icon.png" alt="ZA Player Pro" className="h-9 w-9 rounded-lg" />
            <span className="text-lg font-bold tracking-tight">ZA Player Pro</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-emerald-400"
            data-testid="activation-back-link"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden py-20 md:py-28">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-16 h-[500px] w-[900px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[140px]" />
        </div>

        <div className="mx-auto max-w-3xl px-6 text-center">
          <div className="mb-3 text-xs font-semibold uppercase tracking-[0.25em] text-emerald-400">
            Activation
          </div>
          <h1 className="text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl" data-testid="activation-title">
            Buy or renew your activation
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-neutral-300">
            Your trial has ended, or you&apos;re ready to commit. Pick a plan below —
            one-device, one-time activation, no accounts, no subscriptions to manage.
          </p>
        </div>
      </section>

      {/* Payment return banner (PayFast) — shown when the buyer is bounced
          back to /activation?status=paid|cancelled&ref=… */}
      {payStatus && (
        <section className="px-6 pb-8">
          <div className="mx-auto max-w-3xl">
            <PaymentBanner
              status={payStatus}
              settlement={paySettlement}
              error={paySettleErr}
              onDismiss={() => { setPayStatus(null); setPayRef(null); setPaySettlement(null); }}
            />
          </div>
        </section>
      )}

      {/* Plans */}
      <section className="pb-20">
        {/* Device MAC — required for a new activation so the fresh key is
            bound to the correct device on our end. Prefilled from prior
            visits (localStorage) for convenience. */}
        <div className="mx-auto mb-8 max-w-3xl px-6" data-testid="buyer-mac-panel">
          <div className="rounded-2xl border border-white/10 bg-neutral-950/70 p-5">
            <Label className="text-xs uppercase tracking-widest text-neutral-400">
              Your device MAC address
            </Label>
            <Input
              value={macFormatted}
              onChange={(e) => {
                setMacError("");
                const raw = e.target.value.replace(/[^0-9A-Fa-f]/g, "").toUpperCase().slice(0, 12);
                setBuyerMac(raw);
                try { localStorage.setItem("zapp_buyer_mac", raw); } catch { /* noop */ }
              }}
              placeholder="AA:BB:CC:DD:EE:FF"
              className="mt-2 border-white/10 bg-black font-mono text-lg tracking-widest text-white placeholder:text-neutral-600"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              maxLength={17}
              data-testid="buyer-mac-input"
            />
            <p className="mt-2 text-[11px] text-neutral-500">
              We link your paid key to this exact device. Open the ZA Player Pro app
              on the TV/box you&apos;re buying for and copy the MAC shown on the
              activation screen. 12 hex characters.
            </p>
            {macError && (
              <div
                className="mt-2 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-2 text-xs text-red-100"
                data-testid="buyer-mac-error"
              >
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-none text-red-400" />
                <span>{macError}</span>
              </div>
            )}
            {macValid && !macError && (
              <div className="mt-2 flex items-center gap-2 text-[11px] text-emerald-300/80" data-testid="buyer-mac-valid">
                <Check className="h-3.5 w-3.5" /> Looks good — you can pick a plan below.
              </div>
            )}
          </div>
        </div>

        <div className="mx-auto grid max-w-6xl gap-6 px-6 md:grid-cols-3" data-testid="plan-grid">
          {PLANS.map((plan) => {
            const { id, name, price, priceHint, icon: Icon, features, urlKey, highlight } = plan;
            const legacyHref = cfg?.[urlKey] || "";
            const canBuyDirect = !!legacyHref;
            // Show gateway-picker "Buy" button whenever a paygate is enabled.
            // Fall back to admin-configured link, then to email.
            const showGatewayBuy = anyGatewayEnabled;
            const handleBuyClick = () => {
              if (!macValid) {
                setMacError("Enter your device MAC address before choosing a plan.");
                document.querySelector('[data-testid="buyer-mac-input"]')?.focus();
                window.scrollTo({ top: 0, behavior: "smooth" });
                return;
              }
              setSelectedPlan(plan);
            };
            return (
              <div
                key={id}
                className={`relative rounded-3xl border p-8 transition-all ${
                  highlight
                    ? "border-emerald-400/40 bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 shadow-[0_40px_120px_-10px_rgba(74,222,128,0.2)]"
                    : "border-white/10 bg-neutral-950"
                }`}
                data-testid={`plan-${id}`}
              >
                {highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-emerald-500 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-black">
                    Most popular
                  </div>
                )}

                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400">
                  <Icon className="h-6 w-6" />
                </div>
                <div className="text-lg font-semibold text-white">{name}</div>
                <div className="mt-3 flex items-baseline gap-2">
                  <div className="text-4xl font-extrabold">{price}</div>
                </div>
                <div className="mt-1 text-xs uppercase tracking-widest text-neutral-500">{priceHint}</div>

                <ul className="mt-6 space-y-3 text-sm text-neutral-300">
                  {features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 flex-none text-emerald-400" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {showGatewayBuy ? (
                  <Button
                    onClick={handleBuyClick}
                    className={`mt-8 w-full rounded-full py-6 text-base font-semibold ${
                      highlight
                        ? "bg-emerald-500 text-black hover:bg-emerald-400"
                        : "bg-white/10 text-white hover:bg-white/20"
                    }`}
                    data-testid={`plan-buy-${id}`}
                  >
                    Buy
                  </Button>
                ) : canBuyDirect ? (
                  <a
                    href={macValid ? `${legacyHref}${legacyHref.includes("?") ? "&" : "?"}mac=${macNormalised}` : "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      if (!macValid) {
                        e.preventDefault();
                        setMacError("Enter your device MAC address before choosing a plan.");
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }
                    }}
                  >
                    <Button
                      className={`mt-8 w-full rounded-full py-6 text-base font-semibold ${
                        highlight
                          ? "bg-emerald-500 text-black hover:bg-emerald-400"
                          : "bg-white/10 text-white hover:bg-white/20"
                      }`}
                      data-testid={`plan-buy-${id}`}
                    >
                      Buy now
                    </Button>
                  </a>
                ) : (
                  <a href={`mailto:${contactEmail}?subject=${encodeURIComponent(`Purchase ${name} activation${macValid ? ` — ${macNormalised}` : ""}`)}`}>
                    <Button
                      variant="outline"
                      className="mt-8 w-full rounded-full border-white/20 bg-white/5 py-6 text-base font-semibold text-white hover:bg-white/10"
                      data-testid={`plan-contact-${id}`}
                    >
                      <Mail className="mr-2 h-4 w-4" /> Contact to buy
                    </Button>
                  </a>
                )}
              </div>
            );
          })}
        </div>

        {/* Renew existing key — enter key + pick plan + pay */}
        <div className="mx-auto mt-16 max-w-3xl rounded-3xl border border-white/10 bg-neutral-950 p-8" data-testid="renew-panel">
          <div className="text-center">
            <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <h2 className="text-2xl font-bold">Already have a key?</h2>
            <p className="mx-auto mt-2 max-w-xl text-sm text-neutral-400">
              Enter your existing activation key below, pick a plan, and renew instantly.
              Your device, playlists and settings stay exactly where they are.
            </p>
          </div>

          <form onSubmit={submitRenew} className="mt-6 space-y-4" data-testid="renew-form">
            <div>
              <Label className="text-xs text-neutral-400">Your activation key</Label>
              <Input
                value={renewKey}
                onChange={(e) => setRenewKey(e.target.value.toUpperCase())}
                placeholder="ABCD1234EFGH"
                className="border-white/10 bg-black font-mono text-lg tracking-widest text-white placeholder:text-neutral-600"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                maxLength={40}
                data-testid="renew-key-input"
              />
              {renewInfo && !renewError && (
                <div className="mt-2 text-[11px] text-neutral-500">
                  {renewInfo.is_expired ? (
                    <span className="text-red-400">
                      Expired on {new Date(renewInfo.expires_at).toLocaleDateString()}
                    </span>
                  ) : renewInfo.expires_at ? (
                    <>
                      Current expiry: {new Date(renewInfo.expires_at).toLocaleDateString()}{" "}
                      ({renewInfo.days_remaining} day(s) left)
                    </>
                  ) : (
                    <>Key is active.</>
                  )}
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs text-neutral-400">Renewal plan</Label>
              <div className="mt-2 grid grid-cols-3 gap-2" data-testid="renew-plan-choices">
                {PLANS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setRenewPlanId(p.id)}
                    className={`rounded-xl border p-3 text-left transition-colors ${
                      renewPlanId === p.id
                        ? "border-emerald-400 bg-emerald-500/10"
                        : "border-white/10 bg-black/40 hover:border-white/25"
                    }`}
                    data-testid={`renew-plan-${p.id}`}
                  >
                    <div className="text-xs uppercase tracking-wider text-neutral-400">
                      {p.name}
                    </div>
                    <div className="mt-1 text-lg font-bold text-white">{p.price}</div>
                    <div className="text-[10px] text-neutral-500">{p.priceHint}</div>
                  </button>
                ))}
              </div>
            </div>

            {renewError && (
              <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-100" data-testid="renew-error">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-none text-red-400" />
                <span>{renewError}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={renewBusy}
              className="w-full rounded-full bg-emerald-500 py-6 text-base font-semibold text-black hover:bg-emerald-400"
              data-testid="renew-submit-btn"
            >
              {renewBusy ? (
                <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Checking key…</>
              ) : (
                <><RefreshCw className="mr-2 h-5 w-5" /> Renew &amp; pay</>
              )}
            </Button>

            {!anyGatewayEnabled && (
              <p className="text-center text-[11px] text-neutral-500">
                No payment gateway configured yet.{" "}
                <a
                  href={`mailto:${contactEmail}?subject=${encodeURIComponent("Renew my activation")}`}
                  className="text-emerald-400 underline"
                >
                  Contact support
                </a>
                {" "}to renew manually.
              </p>
            )}
          </form>
        </div>

        {/* Trial banner */}
        <div className="mx-auto mt-16 max-w-3xl rounded-2xl border border-yellow-500/20 bg-yellow-500/5 p-6 text-center text-sm text-yellow-100/80">
          Haven&apos;t tried the app yet?{" "}
          <Link to="/trial" className="font-semibold text-emerald-400 underline hover:text-emerald-300">
            Start a free 5-day trial first
          </Link>
          {" "}— no card required.
        </div>
      </section>

      <footer className="border-t border-white/5 py-10 text-center text-xs text-neutral-500">
        © {new Date().getFullYear()} ZA Player Pro. Player-only application. No content included.
      </footer>

      {/* Gateway picker dialog */}
      <Dialog open={!!selectedPlan} onOpenChange={(o) => !o && setSelectedPlan(null)}>
        <DialogContent className="border-white/10 bg-neutral-900 text-white" data-testid="gateway-picker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-emerald-400" />
              Choose payment method
            </DialogTitle>
            <DialogDescription className="text-neutral-400">
              {selectedPlan
                ? `You're buying the ${selectedPlan.name} plan for ${selectedPlan.price}. Pick the gateway you'd like to pay with.`
                : ""}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {paygate.paypal_enabled && selectedPlan && (
              <a
                href={gatewayUrl("paypal", selectedPlan, selectedPlan.renewKey)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setSelectedPlan(null)}
              >
                <Button
                  className="flex w-full items-center justify-between rounded-xl bg-[#003087] py-6 text-base font-semibold text-white hover:bg-[#001F5C]"
                  data-testid="gateway-pay-paypal"
                >
                  <span className="flex items-center gap-3">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded bg-white text-sm font-bold text-[#003087]">
                      PP
                    </span>
                    Pay with PayPal
                  </span>
                  <span className="text-xs opacity-80">
                    {paygate.paypal_environment === "live" ? "Live" : "Sandbox"}
                  </span>
                </Button>
              </a>
            )}

            {paygate.stripe_enabled && selectedPlan && (
              <a
                href={gatewayUrl("stripe", selectedPlan, selectedPlan.renewKey)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setSelectedPlan(null)}
              >
                <Button
                  className="flex w-full items-center justify-between rounded-xl bg-[#635BFF] py-6 text-base font-semibold text-white hover:bg-[#4B45CC]"
                  data-testid="gateway-pay-stripe"
                >
                  <span className="flex items-center gap-3">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded bg-white text-sm font-bold text-[#635BFF]">
                      S
                    </span>
                    Pay with card (Stripe)
                  </span>
                </Button>
              </a>
            )}

            {paygate.payfast_enabled && selectedPlan && (
              <Button
                onClick={() => submitPayfast(selectedPlan)}
                className="flex w-full items-center justify-between rounded-xl bg-[#ED1C24] py-6 text-base font-semibold text-white hover:bg-[#C41820]"
                data-testid="gateway-pay-payfast"
              >
                <span className="flex items-center gap-3">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded bg-white text-sm font-bold text-[#ED1C24]">
                    PF
                  </span>
                  Pay with PayFast{" "}
                  <span className="text-[10px] font-normal opacity-80">
                    (SA · cards, EFT, SnapScan)
                  </span>
                </span>
                <span className="text-xs opacity-80">
                  {paygate.payfast_environment === "live" ? "Live" : "Sandbox"}
                </span>
              </Button>
            )}

            {!paygate.paypal_enabled && !paygate.stripe_enabled && !paygate.payfast_enabled && (
              <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/5 p-4 text-sm text-yellow-100/80">
                No payment gateways are currently active. Please{" "}
                <a
                  href={`mailto:${contactEmail}`}
                  className="text-emerald-400 underline"
                >
                  contact support
                </a>
                {" "}to complete your purchase.
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSelectedPlan(null)}
              className="border-white/20 bg-white/5 text-white hover:bg-white/10"
              data-testid="gateway-picker-cancel"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
